/* Shared composer styles (the `<Composer>` input box). Scoped under
 * `.nw-composer`, with the design tokens defined there too, so it renders
 * identically in the dashboard SPA and inside the widget's shadow DOM. Follows
 * the netwatch theming convention: a DARK palette is the default; the widget
 * cascades `.nw-theme-light` onto an ancestor, so the `.nw-theme-light
 * .nw-composer` block at EOF flips the tokens. Keep surfaces in tokens (no
 * hard-coded hex / SVG fills). See incidents.css. */

.nw-composer {
  --card: #141b25;
  --line: rgba(255, 255, 255, 0.07);
  --line2: rgba(255, 255, 255, 0.12);
  --ink: #e7ecf3;
  --faint: #5e6b7c;
  --accent: #5b93e8;
  --font-body: 'Barlow', system-ui, sans-serif;

  display: block;
}
.nw-composer * { box-sizing: border-box; }

.nw-composer-box {
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 10px 8px 8px 14px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.nw-composer-box:focus-within {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 14%, transparent);
}

/* Auto-grows: JS sets height to fit content; the inline max-height (from the
   maxRows prop, in em of the 1.45 line-height) caps it and scrolling takes
   over. Keep line-height in sync with LINE_HEIGHT_EM in Composer.tsx. */
.nw-composer textarea {
  width: 100%;
  border: none;
  background: transparent;
  outline: none;
  resize: none;
  overflow-y: auto;
  color: var(--ink);
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.45;
  padding: 0 6px 0 0;
}
.nw-composer textarea::placeholder { color: var(--faint); }
.nw-composer textarea:disabled { opacity: 0.6; cursor: not-allowed; }

.nw-composer-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.nw-composer-accessory {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  color: var(--faint);
  font-size: 12px;
}

.nw-composer-send {
  width: 34px;
  height: 34px;
  flex: none;
  border: none;
  border-radius: 10px;
  display: grid;
  place-items: center;
  cursor: pointer;
  color: #fff;
  background: var(--accent);
  transition: transform 0.15s ease, opacity 0.15s ease;
}
.nw-composer-send:hover:not(:disabled) { transform: scale(1.06); }
.nw-composer-send:active:not(:disabled) { transform: scale(0.95); }
.nw-composer-send:disabled { opacity: 0.4; cursor: not-allowed; }
.nw-composer-send svg { width: 16px; height: 16px; }
/* Streaming: the button flips to a stop control — same accent surface, with a
   larger rounded-square glyph so the state change reads at a glance. */
.nw-composer-send.is-stop svg { width: 18px; height: 18px; }

.nw-composer-footer {
  text-align: center;
  font-size: 11px;
  color: var(--faint);
  margin-top: 8px;
}

/* AI-fallibility line — always the LAST row under the box (mirrors the
   assistant-app convention: small, centered, muted). */
.nw-composer-disclaimer {
  text-align: center;
  font-size: 10.5px;
  color: var(--faint);
  margin-top: 6px;
}

/* --- LIGHT theme (widget sets `.nw-theme-light` on an ancestor) ----------- */
.nw-theme-light .nw-composer {
  --card: #ffffff;
  --line: rgba(17, 24, 39, 0.1);
  --line2: rgba(17, 24, 39, 0.17);
  --ink: #111827;
  --faint: #828b99;
  --accent: #2f6fd0;
}
